import type { RouteObject } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyAnnouncementsManagement,
  CompanyAnnouncementsListPage,
  CompanyAnnouncementDetailPage,
  AnnouncementCreationForm,
} from "@/utils/lazyLoadComponents";
import { store } from "@/store";
import { announcementApi } from "@/services/api.announcements";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_ANNOUNCEMENTS_DYNAMIC_ID_KEY = "id";

export enum COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS {
  ROOT = "announcements",
  NEW = "new",
  DETAILS = `:${COMPANY_ANNOUNCEMENTS_DYNAMIC_ID_KEY}`,
  EDIT = "edit",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyAnnouncementsRoutes: RouteObject = {
  path: COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Announcements" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout (single "All Announcements" tab at root) ─────
    {
      Component: CompanyAnnouncementsManagement,
      handle: { flushContent: true },
      children: [{ index: true, Component: CompanyAnnouncementsListPage }],
    },
    {
      path: COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.NEW,
      handle: { breadcrumb: "New" },
      Component: AnnouncementCreationForm,
    },
    {
      path: COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.DETAILS,
      loader: async ({ params }) => {
        const announcementId = Number(params[COMPANY_ANNOUNCEMENTS_DYNAMIC_ID_KEY]);
        const result = await store.dispatch(announcementApi.endpoints.getAnnouncementById.initiate(announcementId));
        const name = result.data?.data?.announcementData?.subject ?? `Announcement #${announcementId}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Announcement",
      },
      children: [
        { index: true, Component: CompanyAnnouncementDetailPage },
        {
          path: COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.EDIT,
          handle: { breadcrumb: "Edit" },
          Component: AnnouncementCreationForm,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_ANNOUNCEMENTS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.ROOT)),
  NEW: withCompanyPrefix(addSlashes(COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.ROOT, COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.NEW)),
  VIEW: (id: string | number) => withCompanyPrefix(addSlashes(COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.ROOT, String(id))),
  EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.ROOT, String(id), COMPANY_ANNOUNCEMENTS_PATH_SEGMENTS.EDIT),
    ),
});
